import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { useTranslation } from 'react-i18next'
import { ILayout } from 'types/layoutInterfaces'
import { findHideSetting, HideSettingType, hideSettingDetails, isDetailHidden } from '../hideSettings'

interface HideDetailsSettingsProps {
  type: HideSettingType
  layout: ILayout
  handleHideSettings: (data: Record<string, boolean>, type: string) => void
  testIdPrefix?: string
}

const toKebabCase = (value: string) => value.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)

/**
 * Renders a checkbox for each item detail of a print section that the layout can hide.
 *
 * @param {HideSettingType} type - The section's hide setting type.
 * @param {ILayout} layout - The layout whose hide setting is shown and changed.
 * @param {function} handleHideSettings - Saves the section's new hide setting data.
 * @param {string} testIdPrefix - The section name in the checkbox test ids, the type by default.
 * @return {React.ReactElement} The checkboxes.
 */
const HideDetailsSettings: React.FC<HideDetailsSettingsProps> = ({
  type,
  layout,
  handleHideSettings,
  testIdPrefix = type,
}) => {
  const { t } = useTranslation()
  const hideSetting = findHideSetting(layout, type)

  return (
    <Box display="flex" flexDirection="row" flexWrap="wrap" width="100%">
      {hideSettingDetails[type].map(detail => (
        <FormControlLabel
          key={detail}
          control={
            <Checkbox
              data-testid={`checkbox-cvprint-${toKebabCase(testIdPrefix)}-hide-${toKebabCase(detail)}`}
              onChange={(_e, checked: boolean) => handleHideSettings({ ...hideSetting?.data, [detail]: checked }, type)}
              checked={isDetailHidden(hideSetting, detail)}
            />
          }
          label={t(`print.hideSetting.${detail}`)}
        />
      ))}
    </Box>
  )
}

export default HideDetailsSettings
